/**
 * One part of the running order on the playout screen: its tally (On Air in
 * the on-air colour, Next in the next colour, played dimmed), title, planned
 * length — and, on air, the countdown (an overrun counts up) — and its piece
 * strip. The row is the part cursor's target (a click selects); "Set next"
 * names it next.
 */
import { formatCountdown, formatLength } from '../../lib/playout/format.js';
import type { PartView } from '../../lib/playout/viewModel.js';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { PieceStrip } from './PieceStrip.js';

export type PlayoutPartRowProps = {
  readonly part: PartView;
  /** The part cursor is on it. */
  readonly selected: boolean;
  /** On air: the part's clock (elapsed, and remaining — negative once over). */
  readonly onAirClock: { readonly elapsedMs: number; readonly remainingMs: number | null } | null;
  /** The operator may set next (`playout.take`, an active playlist). */
  readonly canSetNext: boolean;
  readonly onSelect: () => void;
  readonly onSetNext: () => void;
};

const ROW_TONE: Readonly<Record<PartView['status'], string>> = {
  onair: 'border-l-onair bg-onair/10',
  next: 'border-l-next bg-next/10',
  played: 'border-l-transparent opacity-60',
  upcoming: 'border-l-transparent',
};

function Tally({ part }: { readonly part: PartView }) {
  if (part.status === 'onair') return <Badge variant="onair">On air</Badge>;
  if (part.status === 'next') return <Badge variant="next">Next</Badge>;
  return null;
}

function Marks({ part }: { readonly part: PartView }) {
  return (
    <>
      {part.orphaned === 'adlib-part' ? <Badge variant="preview">Adlib</Badge> : null}
      {part.orphaned === 'deleted' ? <Badge variant="warning">Deleted — still playing</Badge> : null}
      {part.invalid ? <Badge variant="warning">Invalid</Badge> : null}
      {part.autoNext ? <Badge variant="outline">Auto</Badge> : null}
      {part.untimed ? <Badge variant="outline">Untimed</Badge> : null}
    </>
  );
}

export function PlayoutPartRow({ part, selected, onAirClock, canSetNext, onSelect, onSetNext }: PlayoutPartRowProps) {
  const countdown = onAirClock === null ? null : formatCountdown(onAirClock.remainingMs);
  const tone = part.status === 'onair' ? 'onair' : part.status === 'next' ? 'next' : 'plain';
  return (
    // The row is a click target for the part cursor; the keyboard reaches the same thing through the view's hotkeys (↑/↓).
    <div
      className={cn('grid gap-2 border-l-4 px-3 py-2', ROW_TONE[part.status], selected && 'ring-2 ring-ring ring-inset')}
      data-testid="playout-part"
      data-status={part.status}
      data-part-id={part.partId}
      data-selected={selected}
      aria-current={part.status === 'onair' ? 'true' : undefined}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        <Tally part={part} />
        <span className="min-w-0 flex-1 truncate font-medium" data-testid="part-title">
          {part.title}
        </span>
        <Marks part={part} />
        {countdown === null ? null : (
          <span
            className={cn('rounded px-1.5 font-mono text-sm tabular-nums', countdown.over ? 'bg-onair text-onair-foreground' : 'bg-muted')}
            data-testid="part-row-countdown"
            data-over={countdown.over}
          >
            {countdown.text}
          </span>
        )}
        <span className="w-14 text-right font-mono text-xs text-muted-foreground tabular-nums" title="Planned length">
          {formatLength(part.expectedDurationMs)}
        </span>
        {part.status === 'onair' || part.status === 'next' ? null : (
          <Button
            size="sm"
            variant="outline"
            disabled={!canSetNext}
            onClick={(event) => {
              event.stopPropagation();
              onSetNext();
            }}
            data-testid="set-next"
          >
            Set next
          </Button>
        )}
      </div>
      <PieceStrip lanes={part.lanes} spanMs={part.spanMs} progressMs={onAirClock?.elapsedMs ?? null} tone={tone} />
    </div>
  );
}
